import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Mic, RotateCcw, Home, CheckCircle, Clock, AlertTriangle, Volume2, Play, Pause, Sparkles, Loader2, Award } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ExpressionOraleResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    blocName = 'Expression Orale',
    blocId,
    taches = [],
    recordingDurations = {},
    hasRecorded = {},
    audioDataUrls = {},
    fullTranscript = {},
    timeSpent = 0,
    completedCount = 0
  } = location.state || {};

  const [aiResults, setAiResults] = useState({});
  const [isCorrecting, setIsCorrecting] = useState({});
  const [playingAudio, setPlayingAudio] = useState(null);
  const audioRef = useRef(null);
  const hasAutoStarted = useRef(false);

  const handleCorrectWithAI = async (index) => {
    const tache = taches[index];
    const base64Audio = audioDataUrls[index];
    const transcript = fullTranscript[index];

    if (!base64Audio && !transcript) return;

    setIsCorrecting(prev => ({ ...prev, [index]: true }));

    try {
      const res = await fetch(`${API_URL}/api/ai/correct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          module: 'expression_orale',
          tache_number: tache.tache,
          sujet: tache.sujet,
          instruction: tache.instruction,
          user_response: transcript || '',
          audio_base64: transcript ? null : base64Audio,
          audio_mime_type: 'audio/webm'
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');

      setAiResults(prev => ({ ...prev, [index]: data }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsCorrecting(prev => ({ ...prev, [index]: false }));
    }
  };

  // Auto-start for oral on load
  useEffect(() => {
    if (hasAutoStarted.current) return;
    hasAutoStarted.current = true;

    taches.forEach((_, index) => {
      if (audioDataUrls[index] || fullTranscript[index]) {
        handleCorrectWithAI(index);
      }
    });
  }, [taches, audioDataUrls, fullTranscript]);

  const togglePlay = (index, url) => {
    if (playingAudio === index) {
      audioRef.current.pause();
      setPlayingAudio(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      audioRef.current = new Audio(url);
      audioRef.current.play();
      setPlayingAudio(index);
      audioRef.current.onended = () => setPlayingAudio(null);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins} min ${secs} sec`;
    return `${secs} sec`;
  };

  // Global Score Logic
  const aiScores = Object.values(aiResults).map(r => r.score);
  const globalScore = aiScores.length > 0 ? (aiScores.reduce((a, b) => a + b, 0) / aiScores.length).toFixed(1) : null;
  
  const getGlobalLevel = (s) => {
    if (s >= 18.5) return { cecrl: "C2", nclc: "11-12" };
    if (s >= 16)   return { cecrl: "C1", nclc: "9-10" };
    if (s >= 13)   return { cecrl: "B2", nclc: "7-8" };
    if (s >= 10)   return { cecrl: "B1", nclc: "5-6" };
    if (s >= 7)    return { cecrl: "A2", nclc: "3-4" };
    return { cecrl: "A1", nclc: "1-2" };
  };
  const globalLevel = globalScore ? getGlobalLevel(parseFloat(globalScore)) : null;

  const formatFeedback = (text) => {
    if (!text) return null;
    let formatted = text
      .replace(/^SCORE:.*\n?/gi, '')
      .replace(/^CORRECTION:.*\n?/gi, '')
      .replace(/^FEEDBACK:.*\n?/gi, '')
      .trim();

    const parts = formatted.split(/(\d+\.\s+\*\*.*?\*\*)/g);
    
    return parts.map((part, i) => {
      const isHeader = /(\d+\.\s+\*\*.*?\*\*)/.test(part);
      if (isHeader) {
        return <p key={i} className="font-bold text-red-700 dark:text-red-400 mt-3 mb-1">{part.replace(/\*\*/g, '')}</p>;
      }
      return <p key={i} className="mb-2 last:mb-0 leading-relaxed">{part}</p>;
    });
  };

  const handleRetry = () => {
    navigate(`/modules/expression-orale/exercise?bloc=${blocId}`);
  };

  const handleBackToBlocs = () => {
    navigate('/modules/expression-orale');
  };

  const getTacheStatus = (index) => {
    if (!hasRecorded[index]) return 'empty';
    const duration = recordingDurations[index] || 0;
    const expectedDuration = taches[index]?.duree * 60 || 120;
    if (duration >= expectedDuration * 0.3) return 'valid';
    return 'short';
  };

  const validCount = taches.filter((_, i) => getTacheStatus(i) === 'valid').length;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 animate-in pb-8 px-4">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 rounded-2xl text-center shadow-lg">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Résultats d'Expression Orale</h1>
          <p className="text-orange-100 opacity-90">{blocName}</p>
        </div>

        {/* Global Evaluation Card */}
        {globalScore && (
          <div className="bg-white dark:bg-gray-800 border-2 border-orange-500 rounded-2xl p-6 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Award className="h-24 w-24 text-orange-500" />
            </div>
            <div className="text-center space-y-4">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Évaluation Globale IA</h2>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-orange-50 dark:bg-orange-900/30 px-6 py-3 rounded-2xl border border-orange-100 dark:border-orange-800">
                  <p className="text-xs text-orange-600 dark:text-orange-400 font-bold mb-1">NOTE SUR 20</p>
                  <p className="text-3xl font-black text-orange-700 dark:text-orange-300">{globalScore}</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/30 px-6 py-3 rounded-2xl border border-red-100 dark:border-red-800">
                  <p className="text-xs text-red-600 dark:text-red-400 font-bold mb-1">NIVEAU CECRL</p>
                  <p className="text-3xl font-black text-red-700 dark:text-red-300">{globalLevel.cecrl}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/30 px-6 py-3 rounded-2xl border border-blue-100 dark:border-blue-800">
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-bold mb-1">NIVEAU NCLC</p>
                  <p className="text-3xl font-black text-blue-700 dark:text-blue-300">{globalLevel.nclc}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button onClick={handleRetry} className="flex items-center justify-center space-x-2 py-4 rounded-xl bg-orange-500 text-white hover:bg-orange-600 font-semibold transition-all hover:scale-[1.02] shadow-md">
            <RotateCcw className="h-5 w-5" />
            <span>Refaire l'examen</span>
          </button>
          <button onClick={handleBackToBlocs} className="flex items-center justify-center space-x-2 py-4 rounded-xl border-2 border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-all">
            <Home className="h-5 w-5" />
            <span>Retour aux blocs</span>
          </button>
        </div>

        {/* Detailed Review */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold">Détails des enregistrements</h3>
          
          {taches.map((tache, index) => {
            const status = getTacheStatus(index);
            const duration = recordingDurations[index] || 0;
            const recorded = hasRecorded[index];
            const result = aiResults[index];
            
            return (
              <div key={index} className={`bg-white dark:bg-gray-800 rounded-2xl border-2 overflow-hidden transition-all ${
                status === 'valid' ? 'border-green-200 dark:border-green-900/50' : 'border-orange-200 dark:border-orange-900/50'
              }`}>
                <div className={`px-5 py-4 flex flex-wrap items-center justify-between gap-4 ${
                  status === 'valid' ? 'bg-green-50/50 dark:bg-green-900/10' : 'bg-orange-50/50 dark:bg-orange-900/10'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 text-white flex items-center justify-center font-bold shadow-sm">
                      {tache.tache}
                    </div>
                    <div>
                      <h4 className="font-bold">Tâche {tache.tache}</h4>
                      <p className="text-xs text-muted-foreground">{formatTime(duration)} • {tache.duree} min recommandées</p>
                    </div>
                  </div>
                  
                  {isCorrecting[index] ? (
                    <div className="flex items-center space-x-2 px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-lg animate-pulse">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-xs font-bold uppercase">Analyse IA...</span>
                    </div>
                  ) : result ? (
                    <div className="flex items-center space-x-2">
                      <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-xs font-bold border border-green-200">
                        {result.score}/20
                      </div>
                      <div className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-xs font-bold border border-red-200">
                        {result.cecrl_level}
                      </div>
                      <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-xs font-bold border border-blue-200">
                        NCLC {result.nclc_level}
                      </div>
                    </div>
                  ) : recorded && (
                    <button onClick={() => handleCorrectWithAI(index)} className="text-xs font-bold text-orange-600 hover:underline">
                      Analyser par l'IA
                    </button>
                  )}
                </div>

                <div className="p-5 space-y-4">
                  {fullTranscript[index] ? (
                    <div className="space-y-3">
                      <p className="text-xs font-bold text-gray-400 uppercase mb-2">Dialogue avec l'examinateur :</p>
                      <div className="bg-gray-50 dark:bg-gray-900/40 p-4 rounded-xl border border-gray-100 dark:border-gray-800 max-h-64 overflow-y-auto space-y-4">
                        {fullTranscript[index].split('\n').filter(l => l.trim()).map((line, i) => {
                          const isAi = line.startsWith('Examinateur:');
                          return (
                            <div key={i} className={`flex ${isAi ? 'justify-start' : 'justify-end'}`}>
                              <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                                isAi ? 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-100 shadow-sm' : 'bg-orange-500 text-white shadow-md'
                              }`}>
                                <p className="font-bold text-[10px] uppercase mb-1 opacity-70">{isAi ? 'Examinateur' : 'Candidat'}</p>
                                <p>{line.replace(/^(Examinateur|Candidat): /, '')}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : recorded ? (
                    <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-900/40 p-3 rounded-xl border border-gray-100">
                      <button onClick={() => togglePlay(index, audioDataUrls[index])} className="h-10 w-10 rounded-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center transition shadow-sm">
                        {playingAudio === index ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                      </button>
                      <span className="text-sm font-medium text-gray-600">Écouter mon enregistrement</span>
                    </div>
                  ) : (
                    <p className="text-sm text-red-500 italic">Aucun enregistrement ou dialogue trouvé.</p>
                  )}

                  {result && (
                    <div className="space-y-4 animate-in slide-in-from-bottom-2">
                      <div>
                        <p className="text-xs font-bold text-orange-500 uppercase mb-2 flex items-center space-x-1">
                          <Sparkles className="h-3 w-3" />
                          <span>Retranscription :</span>
                        </p>
                        <div className="bg-white dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 text-sm italic text-gray-700 dark:text-gray-300">
                          "{result.corrected_text}"
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-red-500 uppercase mb-2">Feedback & Analyse :</p>
                        <div className="bg-white dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                          {formatFeedback(result.feedback)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
